import React, { useEffect, useState } from "react";

import {
    TextField,
    Autocomplete,
    CircularProgress
} from "@mui/material";

import AxiosInstance from "../../services/AxiosInstance";
import {
    USERS_URL
} from "../../Constants/Constants";

const UserSearchAutocomplete = ({
    field,
    value,
    onChange,
    selectedUser = null
}) => {

    const [userOptions, setUserOptions] =
        useState(
            selectedUser
                ? [selectedUser]
                : []
        );

    const [loading, setLoading] =
        useState(false);

    const [inputValue, setInputValue] =
        useState(
            selectedUser?.name || ""
        );


    /*
     * Add the existing selected user
     * when edit/view data is loaded.
     */
    useEffect(() => {

        if (!selectedUser?.id) {
            return;
        }

        setUserOptions(prev => {

            const exists = prev.some(
                user =>
                    String(user.id) ===
                    String(selectedUser.id)
            );

            if (exists) {
                return prev;
            }

            return [
                selectedUser,
                ...prev
            ];
        });

        setInputValue(
            selectedUser.name || ""
        );

    }, [selectedUser]);


    /*
     * Find selected user.
     */
    const selectedOption =
        userOptions.find(
            user =>
                String(user.id) ===
                String(value)
        ) ||
        (
            selectedUser &&
            String(selectedUser.id) ===
            String(value)
                ? selectedUser
                : null
        );


    /*
     * Search users.
     */
    const searchUsers = async (
        searchValue
    ) => {

        const search =
            searchValue?.trim();

        if (!search || search.length < 2) {

            setUserOptions(
                selectedUser
                    ? [selectedUser]
                    : []
            );

            return;
        }

        try {

            setLoading(true);

            const response =
                await AxiosInstance.get(
                    `${USERS_URL}/search`,
                    {
                        params: {
                            search
                        }
                    }
                );

            const results =
                response.data || [];

            /*
             * Keep selected user in options.
             */
            if (selectedUser) {

                const exists =
                    results.some(
                        user =>
                            String(user.id) ===
                            String(
                                selectedUser.id
                            )
                    );

                setUserOptions(
                    exists
                        ? results
                        : [
                            selectedUser,
                            ...results
                        ]
                );

            } else {

                setUserOptions(results);
            }

        } catch (error) {

            console.error(
                "Unable to search users",
                error
            );

            setUserOptions(
                selectedUser
                    ? [selectedUser]
                    : []
            );

        } finally {

            setLoading(false);
        }
    };


    return (
        <Autocomplete
            fullWidth

            options={userOptions}

            /*
             * MUI needs the complete user object.
             */
            value={selectedOption || null}

            inputValue={inputValue}

            loading={loading}

            isOptionEqualToValue={(
                option,
                selected
            ) =>
                String(option.id) ===
                String(selected.id)
            }

            getOptionLabel={(option) =>
                option?.name || ""
            }

            onInputChange={(
                event,
                newInputValue,
                reason
            ) => {

                setInputValue(
                    newInputValue
                );

                if (reason === "input") {

                    searchUsers(
                        newInputValue
                    );
                }
            }}

            onChange={(
                event,
                newValue
            ) => {

                /*
                 * Store only ID in lifecycleData.
                 */
                onChange({
                    target: {
                        name: field.name,
                        value:
                            newValue?.id ?? ""
                    }
                });

                setInputValue(
                    newValue?.name || ""
                );
            }}

            renderInput={(params) => (
                <TextField
                    {...params}
                    fullWidth
                    label={field.label}
                    required={field.required}

                    slotProps={{
                        input: {
                            ...params.InputProps,

                            endAdornment: (
                                <>
                                    {loading && (
                                        <CircularProgress
                                            size={20}
                                        />
                                    )}

                                    {
                                        params.InputProps
                                            .endAdornment
                                    }
                                </>
                            )
                        }
                    }}
                />
            )}
        />
    );
};

export default UserSearchAutocomplete;